/**
 * m3 echo Frontend - Markdown Rendering Helpers
 *
 * Security:
 * - LLM output is untrusted input. The raw text is HTML-escaped first, so any
 *   markup the model emits is rendered as literal text instead of being parsed.
 * - Only the small markdown subset below is turned into real HTML.
 * - The result is sanitized with a narrow DOMPurify allowlist before it may
 *   reach a dangerouslySetInnerHTML sink.
 */

import DOMPurify from 'dompurify';

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

const escapeHtml = (text: string): string =>
  text.replace(/[&<>"']/g, (char) => HTML_ESCAPES[char]);

// Formatting-only tags, no attributes and no link/embed elements
const ALLOWED_TAGS = ['strong', 'em', 'h1', 'h2', 'h3', 'br'];

/**
 * Convert a small markdown subset (bold, headings, bullets) into sanitized HTML.
 *
 * Returns an empty string for empty input. The caller is expected to render the
 * result with `whiteSpace: 'pre-wrap'` so line breaks and paragraphs stay intact.
 */
export const renderMarkdownToSafeHtml = (markdown: string | null | undefined): string => {
  if (!markdown) return '';

  const html = escapeHtml(markdown)
    // Bold
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Headings
    .replace(/^### (.*?)$/gm, '<h3>$1</h3>')
    .replace(/^## (.*?)$/gm, '<h2>$1</h2>')
    .replace(/^# (.*?)$/gm, '<h1>$1</h1>')
    // Bullet points
    .replace(/^- (.*?)$/gm, '• $1')
    .replace(/^\* (.*?)$/gm, '• $1');

  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR: [],
    ALLOW_DATA_ATTR: false,
  });
};
